import { supabase } from './supabase';
import type { EnquiryFormData, RequestItem } from '../types';
import { requestItemsToOrderItems, requestItemsToSelectedVases } from './flowNodeRuntime';

export interface SubmitEnquiryArgs {
  formData: EnquiryFormData;
  requestItems: RequestItem[];
  honeypot: string;
  formStartedAt: number;
  sourcePath?: string;
}

export async function submitEnquiryRequest({
  formData,
  requestItems,
  honeypot,
  formStartedAt,
  sourcePath,
}: SubmitEnquiryArgs) {
  let customerId: string | null = null;
  const [firstName, ...restNames] = formData.name.trim().split(/\s+/);
  const lastName = restNames.join(' ').trim();
  const orderItems = requestItemsToOrderItems(requestItems);
  const selectedVases = requestItemsToSelectedVases(requestItems);

  try {
    const consentText = formData.newsletterOptIn
      ? 'Order enquiry submitted. Customer also opted into marketing updates via the enquiry form.'
      : 'Order enquiry submitted for follow-up communication.';

    const { data: contactIntakeData, error: contactIntakeError } = await supabase.functions.invoke(
      'contact-intake',
      {
        body: {
          email: formData.email.trim().toLowerCase(),
          firstName: firstName || formData.name.trim(),
          lastName,
          sourceApp: 'website',
          entryPoint: 'enquiry_form',
          sourcePath:
            sourcePath ??
            (typeof window !== 'undefined' ? window.location.pathname : '/enquiry'),
          newsletterOptIn: Boolean(formData.newsletterOptIn),
          recordNewsletterConsent: Boolean(formData.newsletterOptIn),
          recordEnquiryFollowUpConsent: true,
          consentTextVersion: 'website-enquiry-v1',
          consentText,
        },
      }
    );

    if (contactIntakeError) {
      console.warn('Contact intake failed; continuing with enquiry insert', contactIntakeError);
    } else if (contactIntakeData?.customerId) {
      customerId = contactIntakeData.customerId;
    }
  } catch (contactIntakeError) {
    console.warn('Contact intake failed; continuing with enquiry insert', contactIntakeError);
  }

  const { data, error } = await supabase.functions.invoke('submit-enquiry', {
    body: {
      name: formData.name,
      email: formData.email.trim().toLowerCase(),
      phone: null,
      message: formData.comments || 'No additional comments',
      orderItems,
      selectedVases,
      customerId,
      newsletterOptIn: Boolean(formData.newsletterOptIn),
      sourceApp: 'website',
      entryPoint: 'enquiry_form',
      honeypot,
      formStartedAt,
    },
  });

  if (error) throw error;
  if (data?.error) throw new Error(data.error);

  return data;
}
